import { useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Mail, Loader2, Check } from "lucide-react";
import { z } from "zod";

const emailSchema = z.string().email();

export function NewsletterSignup() {
  const { t } = useTranslation('common');
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      emailSchema.parse(email);
    } catch {
      toast.error(t('newsletter.invalidEmail'));
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase
        .from('email_preferences')
        .insert({
          email: email.trim().toLowerCase(),
          newsletter_consent: true,
          user_type: 'guest',
        });

      if (error) {
        // Handle duplicate email (23505 = unique_violation)
        if (error.code === '23505') {
          toast.info(t('newsletter.alreadySubscribed'));
          setSubmitted(true);
        } else {
          console.error('Newsletter signup error:', error);
          toast.error(t('newsletter.error'));
        }
      } else {
        toast.success(t('newsletter.success'));
        setSubmitted(true);
        setEmail("");
      }
    } catch (err) {
      console.error('Newsletter signup error:', err);
      toast.error(t('newsletter.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex items-center justify-center gap-2 text-primary">
        <Check className="h-5 w-5" />
        <span className="font-medium">{t('newsletter.thankYou')}</span>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="text-center mb-4">
        <h3 className="text-lg font-serif font-semibold mb-1">
          {t('newsletter.title')}
        </h3>
        <p className="text-sm text-muted-foreground">
          {t('newsletter.description')}
        </p>
      </div>
      <form onSubmit={handleSubmit} className="flex gap-2">
        <div className="relative flex-1">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="email"
            placeholder={t('newsletter.placeholder')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="pl-10"
            required
            disabled={isSubmitting}
          />
        </div>
        <Button type="submit" disabled={isSubmitting || !email}>
          {isSubmitting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            t('newsletter.button')
          )}
        </Button>
      </form>
    </div>
  );
}
